import { useSession } from 'src/contexts';
import {
  getGroupTributary,
  getListRules,
  postGroupTributary,
  postRuleTributary,
  deleteRuleTributary,
  getRulesById,
  postUploadXlsFile,
  getXlsFiles,
  postProcessXlsFile,
  getProcessXlsFile,
} from 'src/services/api';
import { classifierFilesSchema } from 'src/schema/ClassiferFiles';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  Box,
  AccordionPanel,
  SimpleGrid,
  Button,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  useToast,
  HStack,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Table,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { UFS } from 'src/constants/ufs';
import {
  REGIME_TRIBUTARIO,
  REMETENTE,
  TIPO_NOTA,
  NATUREZA_OPERACAO,
  FINALIDADE,
  DESTINATARIO,
  formatarData,
  formatarRegime,
  formatarRemetente,
  formatarTipoNota,
  formatarNatureza,
  formatarFinalidade,
  formatarDestinatario,
} from 'src/constants/classiferData';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInput } from 'src/components/FormInput';
import { FormSelect } from 'src/components/FormSelect';
import {
  CirclePlus,
  Download,
  LucideList,
  MoreVertical,
  Trash2,
  TriangleAlert,
  UploadIcon,
} from 'lucide-react';
import styled from '@emotion/styled';
import { IconButton } from '@chakra-ui/react';
import { formatDateToIsoLocal } from 'src/utilities/formatDateIso';
import { htmlContent } from './Components/infoImportFile';

interface Grupo {
  id: string;
  nome: string;
  cnpj?: string;
}

// Valores padrão dos campos (mesmos do tabUnique.tsx)
const DEFAULT_VALUES = {
  grupo: '',
  Descricao: '',
  Regime: '', // Lucro Real
  Remetente: '', // Atacadista
  UfOrigem: '',
  TipoNota: '', // Saída
  Natureza: '', // Venda de mercadoria adquirida ou recebida de terceiros
  Finalidade: '', // Compra para insumo
  Destinatario: '', // Atacadista
  Data: new Date().toISOString().split('T')[0],
  UfDestino: '',
};

interface CenarioTributarioFormData {
  grupo: string;
  Descricao: string;
  Data: string;
  Regime: string;
  Remetente: string;
  UfOrigem: string;
  TipoNota: string;
  Natureza: string;
  Finalidade: string;
  Destinatario: string;
  UfDestino: string;
}

const headerData = [
  { label: 'Nome da regra', key: 'descricao' },
  { label: 'Dt. Vigência', key: 'data' },
  { label: 'Regime Tributário', key: 'regime' },
  { label: 'Remetente', key: 'remetente' },
  { label: 'UF Origem', key: 'ufOrigem' },
  { label: 'Id. Operação', key: 'tipoNota' },
  { label: 'Nat. Operação', key: 'natureza' },
  { label: 'Finalidade', key: 'finalidade' },
  { label: 'Destinatário', key: 'destinatario' },
  { label: 'UF Destino', key: 'ufDestino' },
  { label: '', key: 'acoes' },
];

// Função para remover datas entre parênteses da mensagem
const removeDateFromMessage = (message: string): string => {
  if (!message) return message;
  // Remove padrões como (2026-01-01), (2026/01/01), etc.
  return message
    .replace(/\s*\([0-9]{4}[-/][0-9]{1,2}[-/][0-9]{1,2}\)\s*/g, '')
    .trim();
};

export const TabFile = () => {
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [grupoSelecionado, setGrupoSelecionado] = useState('');
  const [novoGrupoNome, setNovoGrupoNome] = useState('');
  const [errorNome, setErrorNome] = useState(false);
  const [dataRules, setDataRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingRules, setLoadingRules] = useState(false);
  const [isViewingRule, setIsViewingRule] = useState(false);
  const [accordionIndexes, setAccordionIndexes] = useState<number[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [xlsFiles, setXlsFiles] = useState<any[]>([]);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(false);
  const [loadingProcess, setLoadingProcess] = useState<boolean>(false);
  const [idRuleSelected, setIdRuleSelected] = useState<number | null>(null);
  const [processResponse, setProcessResponse] = useState<any>(null);

  const { session } = useSession();
  const toast = useToast();
  const document = session?.company?.document;
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<CenarioTributarioFormData>({
    resolver: yupResolver(classifierFilesSchema),
    defaultValues: DEFAULT_VALUES,
    shouldFocusError: false,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const setDefaultValues = (data: any) => {
    setValue('grupo', data?.idDescricao);
    setValue('Descricao', data?.descricao || '');
    setValue(
      'Data',
      data?.data
        ? formatDateToIsoLocal(data.data)
        : formatDateToIsoLocal(new Date().toISOString().split('T')[0])
    );
    setValue('Regime', data?.regime);
    setValue('Remetente', data?.remetente || '');
    setValue('UfOrigem', data?.ufOrigem || '');
    setValue('TipoNota', data?.tipoNota || 0);
    setValue('Natureza', data?.natureza || '');
    setValue('Finalidade', data?.finalidade || '');
    setValue('Destinatario', data?.destinatario || '');
    setValue('UfDestino', data?.ufDestino || '');
  };

  const changeGroup = (value: string) => {
    setGrupoSelecionado(value);
  };

  const handleCloseModal = () => {
    setNovoGrupoNome('');
    setErrorNome(false);
    onClose();
  };

  const handleOpenModalNewGroup = () => {
    setIsViewingRule(false);
    reset(DEFAULT_VALUES);
    setGrupoSelecionado('');
    setAccordionIndexes([]);
    onOpen();
  };

  const handleCriarGrupo = () => {
    if (!novoGrupoNome.trim()) {
      setErrorNome(true);
      return;
    }

    setErrorNome(false);
    if (Array.isArray(grupos)) {
      const novoGrupo = {
        nome: novoGrupoNome.trim(),
      };
      createGroupTributary(novoGrupo);
    }
  };

  const createGroupTributary = async (data: any) => {
    try {
      const response = await postGroupTributary(data, document);

      setGrupos([...grupos, response?.data]);
      const grupoId = response?.data?.id || '';
      setGrupoSelecionado(grupoId);
      setValue('grupo', grupoId);
      setIsViewingRule(false);
      setNovoGrupoNome('');
      setErrorNome(false);
      onClose();
      toast({
        title: `Grupo ${response?.data?.nome} cadastrado.`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      if (!accordionIndexes.includes(0)) {
        setAccordionIndexes((prev) => [...prev.filter((i) => i !== 0), 0]);
      }
    } catch (error: any) {
      toast({
        title: error?.message || 'Erro ao cadastrar grupo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      console.error('Erro ao criar grupo tributário:', error);
    }
  };

  const createRuleTributary = async (data: any) => {
    setLoading(true);
    try {
      await postRuleTributary(data, document as string);

      toast({
        title: 'Regra tributária salva com sucesso!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      reset();
      setValue('grupo', grupoSelecionado);
      setValue('Data', new Date().toISOString().split('T')[0]);

      if (grupoSelecionado) {
        await listRules(Number(grupoSelecionado));
      }
    } catch (error: any) {
      toast({
        title: error?.message || 'Erro ao salvar regra tributária',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      console.error('Erro ao criar regra tributária:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewScenario = () => {
    setIsViewingRule(false);
    reset(DEFAULT_VALUES);
    setValue('grupo', grupoSelecionado);
    setValue('Data', new Date().toISOString().split('T')[0]);
    // Expandir o accordion "Cenário tributário" (índice 0)
    setAccordionIndexes((prev) => [...prev.filter((i) => i !== 0), 0]);
  };

  const listRules = useCallback(
    async (iddescricao: number) => {
      if (!document) return;

      try {
        const response = await getListRules(document as string, iddescricao);
        setDataRules(response);
        setLoadingRules(false);
      } catch (error) {
        toast({
          title: 'Erro ao carregar Grupo:',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
        console.error('Erro ao carregar regras:', error);
      }
    },
    [document, toast]
  );

  const viewRuleById = useCallback(
    async (idRule: number) => {
      const response = await getRulesById(idRule);
      return response;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast]
  );

  const handleUploadFile = async () => {
    if (!selectedFile) {
      toast({
        title: 'Nenhum arquivo selecionado',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!grupoSelecionado) {
      toast({
        title: 'Selecione um grupo antes de enviar',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!idRuleSelected) {
      toast({
        title: 'Selecione uma regra antes de enviar o arquivo',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setUploadLoading(true);
    try {
      await postUploadXlsFile(
        selectedFile,
        document,
        1,
        Number(grupoSelecionado),
        idRuleSelected,
        selectedFile.name || 'arquivo'
      );

      toast({
        title: 'Arquivo enviado com sucesso!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      setSelectedFile(null);
      // Resetar o input de arquivo
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Recarregar lista de arquivos
      if (document) {
        listXlsFiles(document, idRuleSelected || 0);
      }
    } catch (error: unknown) {
      // Extrair mensagem de erro da resposta da API
      let errorMessage = 'Erro desconhecido';

      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as {
          response?: {
            data?: { message?: string } | string;
            status?: number;
          };
        };

        // Verificar se response.data.message existe (pode ser string ou objeto)
        if (apiError.response?.data) {
          if (
            typeof apiError.response.data === 'object' &&
            apiError.response.data !== null
          ) {
            // Se data é um objeto, verificar se tem message
            if ('message' in apiError.response.data) {
              errorMessage =
                apiError.response.data.message || 'Erro desconhecido';
            }
          } else if (typeof apiError.response.data === 'string') {
            // Se data é uma string, usar diretamente
            errorMessage = apiError.response.data;
          }
        }

        // Fallback: se não encontrou mensagem mas tem status
        if (errorMessage === 'Erro desconhecido' && apiError.response?.status) {
          errorMessage = `Erro ${apiError.response.status}: Falha ao enviar arquivo`;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: 'Erro ao enviar arquivo',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const listXlsFiles = useCallback(
    async (document: string, idRule: number) => {
      if (!document) return;

      try {
        setLoadingFiles(true);
        const response = await getXlsFiles(document, idRule);
        setXlsFiles(response || []);
      } catch (error) {
        toast({
          title: 'Erro ao carregar arquivos',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
        console.error('Erro ao carregar arquivos:', error);
      } finally {
        setLoadingFiles(false);
      }
    },
    [toast]
  );

  const handleDelete = async (ruleId: number) => {
    if (!document) {
      toast({
        title: 'Erro: Documento não encontrado',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    } else {
      const response = await deleteRuleTributary(ruleId, document as string);
      if (response.status === 200) {
        toast({
          title: 'Regra excluída com sucesso!',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        if (grupoSelecionado) {
          await listRules(Number(grupoSelecionado));
        }
      } else {
        toast({
          title: 'Erro ao excluir regra',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
        console.error('Erro ao excluir regra:', response);
      }
    }
  };

  const handleClickRowScenario = async (idRule: number) => {
    const ruleData = await viewRuleById(idRule);

    setIsViewingRule(true);
    setAccordionIndexes([0, 1]);
    if (ruleData) {
      setIdRuleSelected(idRule);
      setDefaultValues(ruleData);
      toast({
        title: 'Cenário selecionado com sucesso!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      setTimeout(() => {
        const sendFileElement = globalThis.document.getElementById('sendFile');
        if (sendFileElement) {
          sendFileElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 300);
    } else {
      setIsViewingRule(false);
      toast({
        title: 'Erro ao carregar cenário:',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleProcessXlsFile = async (idFile: number) => {
    setLoadingProcess(true);
    setProcessResponse(null);

    try {
      const response = await postProcessXlsFile(document as string, idFile);

      // Debug: verificar estrutura da resposta
      console.log('Resposta do processamento:', response);
      console.log('response.data:', response?.data);
      console.log('response.data?.data:', response?.data?.data);

      if (response?.status === 200) {
        // Salvar a resposta - pode estar em response.data ou response.data.data
        const responseData = response?.data?.data || response?.data || null;
        setProcessResponse(responseData);

        console.log('Resposta salva no estado:', responseData);
        console.log(
          'regra_atual?.mensagem:',
          responseData?.regra_atual?.mensagem
        );
        console.log(
          'regra_reforma?.mensagem:',
          responseData?.regra_reforma?.mensagem
        );

        toast({
          title: 'Arquivo processado com sucesso!',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Erro ao processar arquivo',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      toast({
        title: 'Erro ao processar arquivo',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    } finally {
      listXlsFiles(document, idRuleSelected || 0);
      setTimeout(() => {
        setLoadingProcess(false);
      }, 1000);
    }
  };

  const handleDownloadXlsFile = async (idFile: number) => {
    try {
      const fileResponse = await getProcessXlsFile(document as string, idFile);

      if (
        fileResponse?.data &&
        fileResponse?.data?.arquivo &&
        fileResponse?.data?.nomeArquivo &&
        fileResponse?.status === 200
      ) {
        const { arquivo, nomeArquivo } = fileResponse?.data;

        const binaryString = atob(arquivo);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        // Criar link de download
        const url = window.URL.createObjectURL(blob);
        const linkElement = globalThis.document.createElement('a');
        linkElement.href = url;
        linkElement.download = nomeArquivo;
        globalThis.document.body.appendChild(linkElement);
        linkElement.click();

        // Limpar
        globalThis.document.body.removeChild(linkElement);
        window.URL.revokeObjectURL(url);

        toast({
          title: 'Arquivo baixado com sucesso!',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        throw new Error('Dados do arquivo não encontrados na resposta');
      }
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      toast({
        title: 'Erro ao baixar arquivo',
        description: 'Não foi possível processar o download',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onSubmit: SubmitHandler<CenarioTributarioFormData> = (data) => {
    const payload = {
      ...data,
      cnpjCpf: document,
      idDescricao: grupoSelecionado,
    };
    if (!grupoSelecionado) {
      toast({
        title: 'Erro: Selecione um grupo antes de salvar',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    createRuleTributary(payload);
  };

  useEffect(() => {
    // lista de grupos
    const getGroups = async () => {
      if (!document) return;

      try {
        const response = await getGroupTributary(document as string);
        if (response?.status === 200 && response?.data) {
          setGrupos(response?.data || []);
        } else {
          setGrupos([]);
        }
      } catch (error) {
        toast({
          title: 'Erro ao carregar grupos:',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
        console.error('Erro ao carregar grupos:', error);
      }
    };
    getGroups();
  }, [document, toast]);

  // Sincronizar grupoSelecionado com o form quando mudar externamente
  useEffect(() => {
    if (grupoSelecionado && document) {
      setValue('grupo', grupoSelecionado);
      const grupoId = Number(grupoSelecionado);
      if (!isNaN(grupoId)) {
        listRules(grupoId);
      }
    }

    if (loadingRules && grupoSelecionado) {
      const grupoId = Number(grupoSelecionado);
      listRules(grupoId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupoSelecionado, document, setValue, loadingRules, listRules]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      toast({
        title: 'Existem campos obrigatórios que não foram preenchidos',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  }, [errors, toast]);

  // Carregar arquivos XLS quando o documento estiver disponível
  useEffect(() => {
    if (document && idRuleSelected) {
      listXlsFiles(document, idRuleSelected || 0);
    }
  }, [document, listXlsFiles, idRuleSelected]);

  return (
    <TabConsultaBox className="tabconsulta" width="100%">
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Seção 1. Grupo  */}
        <Box id="select-grupo" className="section-group">
          <Box flex="1" textAlign="left" className="section-group-header">
            <Text fontSize={'16px'} color={'#3A86FF'} fontWeight={'bold'}>
              1. Grupo
            </Text>
          </Box>
          <Flex className="section-group-content" gap={4} alignItems="flex-end">
            <Box flex={1}>
              <FormSelect
                label="Selecionar grupo"
                isRequired
                error={errors.grupo}
                {...register('grupo', {
                  onChange: (e) => {
                    const value = e.target.value;
                    changeGroup(value);
                  },
                })}
              >
                <option value="">Selecione...</option>
                {Array.isArray(grupos) &&
                  grupos
                    .filter((grupo) => grupo.id && grupo.nome)
                    .map((grupo) => (
                      <option key={grupo.id} value={grupo.id}>
                        {grupo.nome}
                      </option>
                    ))}
              </FormSelect>
            </Box>
            <Button
              bg="#166dd7"
              color="white"
              _hover={{ bg: '#1155bb' }}
              onClick={handleOpenModalNewGroup}
              mb={0}
              height="36px"
            >
              Criar novo grupo
            </Button>
          </Flex>
        </Box>

        {/* Seção 2. Cenário Tributário */}
        <Box>
          <Accordion
            index={accordionIndexes.includes(0) ? [0] : []}
            onChange={(expandedIndex) => {
              const indexes = expandedIndex as number[];
              if (indexes.includes(0)) {
                setAccordionIndexes((prev) => [
                  ...prev.filter((i) => i !== 0),
                  0,
                ]);
              } else {
                setAccordionIndexes((prev) => prev.filter((i) => i !== 0));
              }
            }}
            allowMultiple
          >
            <AccordionItem>
              <h2>
                <AccordionButton sx={{ bg: '#F7F8FC' }}>
                  <Box
                    flex="1"
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text
                      fontSize={'16px'}
                      color={'#3A86FF'}
                      fontWeight={'bold'}
                    >
                      2. Cenário tributário
                    </Text>
                    <Flex
                      sx={{
                        fontSize: '12px',
                        color: '#303030',
                        border: '1px solid #3A86FF',
                        borderRadius: '4px',
                        padding: '4px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                      }}
                      onClick={(e: any) => {
                        handleAddNewScenario();
                        e.stopPropagation();
                      }}
                    >
                      <CirclePlus width={18} height={18} color="#3A86FF" />
                      <Text color="#3A86FF"> Adicionar cenário </Text>
                    </Flex>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={6} sx={{ padding: '0px' }}>
                <Box className="section-group-content">
                  {/* Linha 1: Nome da regra e Data da Vigência */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                    <FormInput
                      label="Nome da regra"
                      placeholder="Nome da descrição"
                      error={errors.Descricao}
                      isRequired
                      isDisabled={isViewingRule}
                      {...register('Descricao')}
                    />
                    <FormInput
                      label="Data da Vigência"
                      type="date"
                      error={errors.Data}
                      isRequired
                      isDisabled={isViewingRule}
                      {...register('Data')}
                    />
                  </SimpleGrid>

                  {/* Linha 2: Regime Tributário e Remetente */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                    <FormSelect
                      label="Regime Tributário"
                      isRequired
                      error={errors.Regime}
                      isDisabled={isViewingRule}
                      {...register('Regime')}
                    >
                      {REGIME_TRIBUTARIO.map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </FormSelect>
                    <FormSelect
                      label="Remetente"
                      isRequired
                      error={errors.Remetente}
                      isDisabled={isViewingRule}
                      {...register('Remetente')}
                    >
                      <option value="">Selecione...</option>
                      {REMETENTE.map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </FormSelect>
                  </SimpleGrid>

                  {/* Linha 3: UF Origem e Identificador da Operação */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                    <FormSelect
                      label="UF Origem"
                      isRequired
                      error={errors.UfOrigem}
                      isDisabled={isViewingRule}
                      {...register('UfOrigem')}
                    >
                      <option value="">Selecione...</option>
                      {UFS.map((uf) => (
                        <option key={uf.value} value={uf.value}>
                          {uf.label}
                        </option>
                      ))}
                    </FormSelect>
                    <FormSelect
                      label="Identificador da Operação"
                      isRequired
                      error={errors.TipoNota}
                      isDisabled={isViewingRule}
                      {...register('TipoNota')}
                    >
                      <option value="">Selecione...</option>
                      {TIPO_NOTA.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </FormSelect>
                  </SimpleGrid>

                  {/* Linha 4: Natureza da operação e Finalidade */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                    <FormSelect
                      label="Natureza da operação"
                      isRequired
                      error={errors.Natureza}
                      isDisabled={isViewingRule}
                      {...register('Natureza')}
                    >
                      <option value="">Selecione...</option>
                      {NATUREZA_OPERACAO.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </FormSelect>
                    <FormSelect
                      label="Finalidade"
                      isRequired
                      error={errors.Finalidade}
                      isDisabled={isViewingRule}
                      {...register('Finalidade')}
                    >
                      <option value="">Selecione...</option>
                      {FINALIDADE.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </FormSelect>
                  </SimpleGrid>

                  {/* Linha 5: Destinatário e UF Destino */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
                    <FormSelect
                      label="Destinatário"
                      isRequired
                      error={errors.Destinatario}
                      isDisabled={isViewingRule}
                      {...register('Destinatario')}
                    >
                      <option value="">Selecione...</option>
                      {DESTINATARIO.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </FormSelect>
                    <FormSelect
                      label="UF Destino"
                      isRequired
                      error={errors.UfDestino}
                      isDisabled={isViewingRule}
                      {...register('UfDestino')}
                    >
                      <option value="">Selecione...</option>
                      {UFS.map((uf) => (
                        <option key={uf.value} value={uf.value}>
                          {uf.label}
                        </option>
                      ))}
                    </FormSelect>
                  </SimpleGrid>
                </Box>
                {/* Botões Cancelar e Salvar cenário */}
                <Flex justifyContent="flex-end" className="actions-cenario">
                  <Button
                    type="button"
                    variant="ghost"
                    color="#166dd7"
                    _hover={{ bg: '#f0f0f0' }}
                    px={6}
                    py={6}
                    fontSize="md"
                    fontWeight="600"
                    onClick={() => reset()}
                    isLoading={loading}
                    isDisabled={isViewingRule}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    bg="#166dd7"
                    color="white"
                    _hover={{ bg: '#1155bb' }}
                    px={6}
                    py={6}
                    fontSize="md"
                    fontWeight="600"
                    isLoading={loading}
                    isDisabled={isViewingRule}
                  >
                    Salvar cenário
                  </Button>
                </Flex>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
      </form>
      {/* Seção 3. importar arquivo & processar*/}
      <Box>
        <Accordion
          index={accordionIndexes.includes(1) ? [0] : []}
          onChange={(expandedIndex) => {
            const indexes = expandedIndex as number[];
            if (indexes.length > 0) {
              setAccordionIndexes((prev) => [
                ...prev.filter((i) => i !== 1),
                1,
              ]);
            } else {
              setAccordionIndexes((prev) => prev.filter((i) => i !== 1));
            }
          }}
          allowMultiple
          allowToggle
        >
          <AccordionItem>
            <AccordionButton sx={{ bg: '#F7F8FC' }}>
              <Box flex="1" textAlign="left">
                <HStack alignItems={'center'} justifyContent={'space-between'}>
                  <Text fontSize={'16px'} color={'#3A86FF'} fontWeight={'bold'}>
                    3. Importar planilha
                  </Text>
                  <Box>
                    <Text
                      fontSize={'12px'}
                      color={'#303030'}
                      dangerouslySetInnerHTML={{
                        __html: `Faça o download da Planilha Padrão: <a 
                        href="https://docs.google.com/spreadsheets/d/1H6SYCGPAYiFcDqtasARWHhbSoYOxjLTm/edit?usp=sharing&ouid=104797142836196530488&rtpof=true&sd=true" 
                        download 
                        style="color: #3A86FF; font-weight: bold; text-decoration: underline;"
                        target="_blank"
                      >
                        clique aqui
                      </a>`,
                      }}
                    />
                  </Box>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            {/* input de arquivo */}
            <AccordionPanel>
              <Box>
                <Box>
                  <Text
                    id="txtInfo"
                    fontSize={'12px'}
                    color={'#303030'}
                    dangerouslySetInnerHTML={{
                      __html: htmlContent,
                    }}
                  />
                </Box>
                <Box
                  id="sendFile"
                  sx={{
                    mt: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    border: '1px solid #E5E5E5',
                    borderRadius: '8px',
                    padding: '16px',
                  }}
                >
                  <FormControl>
                    <FormLabel fontSize="sm">
                      Selecione o arquivo Excel
                    </FormLabel>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".xls,.xlsx"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setSelectedFile(file);
                        }
                      }}
                      sx={{
                        padding: '4px',
                        fontSize: '14px',
                      }}
                    />
                    {selectedFile && (
                      <Text fontSize="xs" color="gray.600" mt={2}>
                        Arquivo selecionado: {selectedFile.name}
                      </Text>
                    )}
                  </FormControl>
                  <Flex
                    justifyContent="center"
                    alignItems="center"
                    width={'100%'}
                  >
                    <Button
                      leftIcon={<UploadIcon />}
                      variant="outline"
                      color="#3A86FF"
                      borderColor="#3A86FF"
                      onClick={handleUploadFile}
                      isLoading={uploadLoading}
                      isDisabled={!selectedFile}
                      _hover={{ bg: '#3A86FF', color: 'white' }}
                      w="200px"
                    >
                      Enviar
                    </Button>
                  </Flex>
                </Box>
              </Box>
            </AccordionPanel>
            {/* lista de arquivos */}
            <AccordionPanel>
              {loadingFiles ? (
                <Flex justifyContent="center" py={4}>
                  <Spinner size="md" color="primary.pure" />
                </Flex>
              ) : (
                <Box>
                  {/* Arquivos pendentes - tipo=1 */}
                  <Box mb={6}>
                    <HStack mb={3}>
                      <Text
                        fontSize={'14px'}
                        color={'#3A86FF'}
                        fontWeight={'bold'}
                      >
                        Arquivos pendentes
                      </Text>
                    </HStack>
                    {xlsFiles.filter((file) => file.tipo === 1).length === 0 ? (
                      <Text fontSize="12px" color="gray.500">
                        Nenhum arquivo pendente
                      </Text>
                    ) : (
                      <Table
                        size="sm"
                        variant="striped"
                        className="files-table"
                      >
                        <Thead>
                          <Tr>
                            <Th fontSize="12px" color="#3A86FF">
                              Nome do arquivo
                            </Th>
                            <Th fontSize="12px" color="#3A86FF">
                              Data enviada
                            </Th>
                            <Th fontSize="12px" color="#3A86FF">
                              Ação
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {xlsFiles
                            .filter((file) => file.tipo === 1)
                            .map((file, index) => (
                              <Tr key={file.id || index}>
                                <Td fontSize="12px">{file.nome || '-'}</Td>
                                <Td fontSize="12px">
                                  {file.data
                                    ? new Date(file.data).toLocaleDateString(
                                        'pt-BR'
                                      )
                                    : '-'}
                                </Td>
                                <Td>
                                  <Button
                                    size="sm"
                                    variant="link"
                                    colorScheme="#303030"
                                    fontSize="11px"
                                    leftIcon={
                                      <TriangleAlert
                                        size={14}
                                        color="#A1A1AA"
                                      />
                                    }
                                    onClick={() =>
                                      handleProcessXlsFile(file.id)
                                    }
                                  >
                                    Clique para processar
                                  </Button>
                                </Td>
                              </Tr>
                            ))}
                        </Tbody>
                      </Table>
                    )}
                  </Box>

                  {/* Arquivos retornados - tipo=2 */}
                  <Box>
                    <HStack mb={3}>
                      <Text
                        fontSize={'14px'}
                        color={'#3A86FF'}
                        fontWeight={'bold'}
                      >
                        Arquivos retornados
                      </Text>
                    </HStack>
                    {xlsFiles.filter((file) => file.tipo === 2).length === 0 ? (
                      <Text fontSize="12px" color="gray.500">
                        Nenhum arquivo retornado
                      </Text>
                    ) : (
                      <Table
                        size="sm"
                        variant="striped"
                        className="files-table"
                      >
                        <Thead>
                          <Tr>
                            <Th fontSize="12px" color="#3A86FF">
                              Nome do arquivo
                            </Th>
                            <Th fontSize="12px" color="#3A86FF">
                              Data enviada
                            </Th>
                            <Th fontSize="12px" color="#3A86FF">
                              Ação
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {xlsFiles
                            .filter((file) => file.tipo === 2)
                            .map((file, index) => (
                              <Tr key={file.id || index}>
                                <Td fontSize="12px">{file.nome || '-'}</Td>
                                <Td fontSize="12px">
                                  {file.data
                                    ? new Date(file.data).toLocaleDateString(
                                        'pt-BR'
                                      )
                                    : '-'}
                                </Td>
                                <Td>
                                  <Button
                                    size="sm"
                                    variant="link"
                                    colorScheme="#303030"
                                    fontSize="11px"
                                    leftIcon={
                                      <Download size={14} color="#A1A1AA" />
                                    }
                                    onClick={() =>
                                      handleDownloadXlsFile(file.id)
                                    }
                                  >
                                    Download
                                  </Button>
                                </Td>
                              </Tr>
                            ))}
                        </Tbody>
                      </Table>
                    )}
                  </Box>
                </Box>
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Box>

      {/* Área de resultado do processamento */}
      {processResponse && (
        <Box
          mt={6}
          width="100%"
          p={6}
          bg="#fbfbfb"
          borderRadius="8px"
          border="1px solid #e5e5e5"
        >
          {/* Mensagem de sucesso */}
          <Box
            bg="#e8f5e9"
            color="#2e7d32"
            p={4}
            borderRadius="6px"
            mb={
              processResponse?.regra_atual?.mensagem ||
              processResponse?.regra_reforma?.mensagem
                ? 4
                : 0
            }
            borderLeft="4px solid #2e7d32"
          >
            <Text fontWeight="bold">✅ Consulta realizada com sucesso!</Text>
          </Box>

          {/* Mensagem de warning de regra_atual se existir */}
          {processResponse?.regra_atual?.mensagem && (
            <Box
              bg="#fff3cd"
              color="#856404"
              p={4}
              borderRadius="6px"
              mb={processResponse?.regra_reforma?.mensagem ? 4 : 0}
              borderLeft="4px solid #ffc107"
            >
              <Text fontWeight="bold" mb={2}>
                ⚠️ Aviso
              </Text>
              <Text>
                {removeDateFromMessage(processResponse.regra_atual.mensagem)}
              </Text>
            </Box>
          )}

          {/* Mensagem de warning de regra_reforma se existir */}
          {processResponse?.regra_reforma?.mensagem && (
            <Box
              bg="#fff3cd"
              color="#856404"
              p={4}
              borderRadius="6px"
              borderLeft="4px solid #ffc107"
            >
              <Text fontWeight="bold" mb={2}>
                ⚠️ Aviso
              </Text>
              <Text>
                {removeDateFromMessage(processResponse.regra_reforma.mensagem)}
              </Text>
            </Box>
          )}
        </Box>
      )}

      {/* lista cenarios */}
      <Box className="section-cenarios">
        <HStack w="100%" mb={4}>
          <LucideList color="#3A86FF" />
          <Text fontSize={'2xl'} color={'#3A86FF'} fontWeight={'bold'}>
            Cenários
          </Text>
        </HStack>
        <Box
          style={{
            width: '100%',
            overflowY: 'auto',
            overflowX: 'auto',
            maxHeight: 'calc(100vh - 500px)',
            minHeight: '200px',
          }}
        >
          <Table
            size="sm"
            style={{ minWidth: '100%' }}
            variant="striped"
            w="full"
          >
            <Thead
              position="sticky"
              top="0"
              bg="high.pure"
              sx={{ borderBottom: '1px solid #E5E5E5' }}
            >
              <Tr>
                {headerData.map((header) => (
                  <Th
                    key={header.key}
                    fontSize="13px"
                    fontWeight="400"
                    color="#3A86FF"
                    borderWidth="0px"
                    textTransform="none"
                    px={2}
                  >
                    {header.label}
                  </Th>
                ))}
              </Tr>
            </Thead>

            {loading ? (
              <Tbody>
                <Tr>
                  <Td colSpan={headerData.length} textAlign="center">
                    <Spinner size="sm" color="primary.pure" />
                  </Td>
                </Tr>
              </Tbody>
            ) : (
              <Tbody>
                {!grupoSelecionado || grupoSelecionado === '' ? (
                  <Tr>
                    <Td
                      colSpan={headerData.length}
                      textAlign="center"
                      bg="blue.50"
                      py={4}
                    >
                      <Text color="#3A86FF" fontSize="14px" fontWeight="600">
                        Selecione um grupo para ver as regras
                      </Text>
                    </Td>
                  </Tr>
                ) : dataRules.length === 0 ? (
                  <Tr>
                    <Td colSpan={headerData.length} textAlign="center" py={4}>
                      <Text color="#3A86FF  " fontSize="14px">
                        Nenhum registro encontrado
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  dataRules.map((rule, index) => (
                    <Tr
                      key={rule.id || index}
                      sx={{
                        '& td': {
                          fontSize: '12px',
                          px: 2,
                        },
                        '& .font-size-10': { fontSize: '10px' },
                      }}
                      onClick={() => handleClickRowScenario(rule.id || 0)}
                      cursor={'pointer'}
                    >
                      <Td>{rule.descricao || '-'}</Td>
                      <Td>{formatarData(rule.data)}</Td>
                      <Td>{formatarRegime(rule.regime)}</Td>
                      <Td>{formatarRemetente(rule.remetente)}</Td>
                      <Td>{rule.ufOrigem || '-'}</Td>
                      <Td>{formatarTipoNota(rule.tipoNota)}</Td>
                      <Td>{formatarNatureza(rule.natureza)}</Td>
                      <Td>{formatarFinalidade(rule.finalidade)}</Td>
                      <Td>{formatarDestinatario(rule.destinatario)}</Td>
                      <Td>{rule.ufDestino || '-'}</Td>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            aria-label="Ações"
                            icon={<MoreVertical size={18} />}
                            variant="ghost"
                            size="sm"
                            colorScheme="gray"
                          />
                          <MenuList sx={{ minWidth: '30px', pt: 0, pb: 0 }}>
                            <MenuItem
                              icon={<Trash2 size={16} />}
                              onClick={() => handleDelete(rule.id)}
                              color="red.500"
                            >
                              Excluir
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            )}
          </Table>
          <Flex justifyContent="flex-end" mt={4}>
            <Button
              variant="solid"
              bg="#166dd7"
              size={'sm'}
              color="white"
              _hover={{ bg: '#1155bb' }}
              onClick={() => {
                setAccordionIndexes([]);
                setTimeout(() => {
                  const selectGrupoElement =
                    globalThis.document.getElementById('select-grupo');
                  if (selectGrupoElement) {
                    selectGrupoElement.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }
                }, 300);
              }}
            >
              Voltar
            </Button>
          </Flex>
        </Box>
      </Box>
      {/* Modal */}
      <Modal isOpen={isOpen || loadingProcess} onClose={handleCloseModal}>
        <ModalOverlay />
        {loadingProcess ? (
          <ModalContent>
            <ModalBody
              pb={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pt: '60px',
                pb: '60px',
                gap: '20px',
              }}
            >
              <Text
                fontSize="20px"
                color="#292929"
                fontWeight="bold"
                fontFamily="Inter"
              >
                Aguarde o processamento...
              </Text>
              <Spinner
                thickness="6px"
                speed="0.84s"
                emptyColor="gray.200"
                color="#3A86FF"
                w="80px"
                h="80px"
              />
            </ModalBody>
          </ModalContent>
        ) : (
          <ModalContent>
            <ModalHeader>Novo grupo</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Box mb={4}>
                <FormControl id="novoGrupo" isInvalid={errorNome} isRequired>
                  <FormLabel fontSize="xs" mb="3px">
                    Nome
                  </FormLabel>
                  <Input
                    name="novoGrupo"
                    placeholder="Digite o nome do grupo"
                    value={novoGrupoNome}
                    onChange={(e) => {
                      setNovoGrupoNome(e.target.value);
                      if (errorNome) {
                        setErrorNome(false);
                      }
                    }}
                    h="36px"
                    bgColor="high.pure"
                    borderRadius="4px"
                    fontSize="sm"
                    errorBorderColor="red.500"
                  />
                  {errorNome && (
                    <FormErrorMessage mt="4px" fontSize="xs">
                      Nome do grupo é obrigatório
                    </FormErrorMessage>
                  )}
                </FormControl>
              </Box>
              <Flex justifyContent="flex-end" gap={4}>
                <Button variant="ghost" onClick={handleCloseModal}>
                  Cancelar
                </Button>
                <Button
                  bg="#166dd7"
                  color="white"
                  _hover={{ bg: '#1155bb' }}
                  onClick={handleCriarGrupo}
                >
                  Criar
                </Button>
              </Flex>
            </ModalBody>
          </ModalContent>
        )}
      </Modal>
    </TabConsultaBox>
  );
};

const TabConsultaBox = styled(Box)`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin-bottom: 16px;
  width: 100%;
  .section-group {
    background-color: ##ffffff;
  }
  .section-group-header {
    background-color: #f7f8fc;
    padding: 10px 20px;
  }
  .section-group-content {
    padding: 16px 25px;
  }
  .actions-cenario {
    background-color: #f7f8fc;
    padding: 10px 20px;
    margin-bottom: 10px;
    gap: 5px;
  }
  .section-cenarios {
    padding: 25px 16px;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .files-table {
    tbody tr {
      height: 20px;
    }
    td {
      padding: 2px 6px !important;
    }
  }
  .chakra-form-control {
    margin-top: 0 !important;
  }
`;

